import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Loader2, AlertTriangle } from 'lucide-react';
import { useOntologyStore } from '@/stores/useOntologyStore';
import type { OntologyConcept } from '@/types/ontology';

interface DeleteConceptDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  concept: OntologyConcept | null;
  token?: string | null;
  onSuccess?: () => void;
}

export function DeleteConceptDialog({ open, onOpenChange, concept, token, onSuccess }: DeleteConceptDialogProps) {
  const { deleteConcept, loading } = useOntologyStore();
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!concept) return;
    setError(null);

    try {
      await deleteConcept(concept.id, token);
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete concept');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle size={20} />
            Delete Concept
          </DialogTitle>
          <DialogDescription>
            This action cannot be undone. The concept will be permanently removed from the ontology.
          </DialogDescription>
        </DialogHeader>

        {concept && (
          <div className="bg-muted border border-border rounded p-3 space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase">Name:</span>
              <span className="text-sm font-mono">{concept.name}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase">Type:</span>
              <span className="text-xs uppercase bg-blue-100 text-blue-700 border border-blue-300 px-1.5 py-0.5 rounded">{concept.metaType}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground uppercase">Status:</span>
              <span className="text-xs uppercase bg-green-100 text-green-700 border border-green-300 px-1.5 py-0.5 rounded">{concept.status}</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-xs">
            {error}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={loading}>
            {loading && <Loader2 size={14} className="animate-spin mr-1" />}
            Delete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
